import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Compass, Loader2, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.status === 401 ? t("auth.invalidCredentials") : t("auth.genericError"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-5">
      <Link to="/" className="flex items-center gap-2 mb-8">
        <div className="h-9 w-9 rounded-lg brand-bg flex items-center justify-center"><Compass className="h-5 w-5 text-white" /></div>
        <span className="font-display font-bold text-xl tracking-tight">{t("common.appName")}</span>
      </Link>
      <div className="w-full max-w-sm bg-card border border-border rounded-2xl p-8 shadow-sm">
        <h1 className="text-2xl font-bold font-display tracking-tight">{t("auth.welcomeBack")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("auth.loginSubtitle")}</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-xs">{t("auth.email")}</Label>
            <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
          </div>
          <div>
            <Label className="text-xs">{t("auth.password")}</Label>
            <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
          </div>
          {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
          <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full brand-bg text-white hover:opacity-90">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.login")}
          </Button>
        </form>
        <p className="text-sm text-muted-foreground mt-5 text-center">
          {t("auth.noAccount")} <Link to="/register" className="brand-text font-medium">{t("common.startForFree")}</Link>
        </p>
        <div className="mt-5 pt-4 border-t border-border text-xs text-muted-foreground text-center bg-secondary/50 rounded-lg p-2">
          {t("auth.demoHint")}
        </div>
      </div>
      <p className="mt-6 text-xs text-muted-foreground flex items-center gap-1"><Lock className="h-3 w-3" /> {t("auth.privacyNote")}</p>
    </div>
  );
}
